import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, HandCoins } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { listReceivablesQueryOptions } from "@/api/financial";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useFarmScope } from "@/hooks/useFarmScope";
import { formatMoney } from "@/utils/financial";
import { StatCard } from "../-components/StatCard";
import { ReceivableStatusBadge } from "./-components/ReceivableBadges";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/receivables/",
)({
	component: ReceivablesListPage,
});

function ReceivablesListPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const scope = useFarmScope();
	const { formatDateOnly } = useDateFormatter();

	const establishmentId =
		scope.kind === "single" ? scope.establishmentId : undefined;

	const queryParams = useMemo(
		() => ({
			...(establishmentId ? { establishmentId } : {}),
		}),
		[establishmentId],
	);

	const { data, isLoading } = useQuery(
		listReceivablesQueryOptions(orgId, queryParams),
	);

	const currency = data?.currency ?? "BRL";
	const summary = data?.summary;

	// Overdue first, then by nearest due date, then by sale date.
	const rows = useMemo(() => {
		const list = data?.data ?? [];
		return [...list].sort((a, b) => {
			if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
			if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
			if (a.dueDate) return -1;
			if (b.dueDate) return 1;
			return a.saleDate.localeCompare(b.saleDate);
		});
	}, [data?.data]);

	return (
		<div className="flex flex-col gap-4">
			<p className="text-sm text-base-content/70">
				{t("financial.receivables.subtitle")}
			</p>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
				<StatCard
					label={t("financial.receivables.summary.totalOutstanding")}
					value={formatMoney(summary?.totalOutstanding ?? "0", currency)}
					icon={HandCoins}
				/>
				<StatCard
					label={t("financial.receivables.summary.overdueCount")}
					value={String(summary?.overdueCount ?? 0)}
					icon={AlertTriangle}
					tone={summary && summary.overdueCount > 0 ? "negative" : "neutral"}
				/>
				<StatCard
					label={t("financial.receivables.summary.overdueAmount")}
					value={formatMoney(summary?.overdueAmount ?? "0", currency)}
					icon={AlertTriangle}
					tone={
						summary && Number.parseFloat(summary.overdueAmount) > 0
							? "negative"
							: "neutral"
					}
				/>
			</div>

			<div className="card bg-base-100 shadow-md">
				<div className="card-body">
					{isLoading ? (
						<div className="flex justify-center py-16">
							<LoadingSpinner />
						</div>
					) : rows.length === 0 ? (
						<div className="py-12 text-center">
							<p className="font-medium">
								{t("financial.receivables.empty.title")}
							</p>
							<p className="mt-1 text-sm text-base-content/60">
								{t("financial.receivables.empty.description")}
							</p>
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>
										{t("financial.receivables.columns.buyer")}
									</TableHead>
									<TableHead className="hidden sm:table-cell">
										{t("financial.receivables.columns.saleDate")}
									</TableHead>
									<TableHead>
										{t("financial.receivables.columns.dueDate")}
									</TableHead>
									<TableHead className="text-right hidden md:table-cell">
										{t("financial.receivables.columns.expected")}
									</TableHead>
									<TableHead className="text-right hidden md:table-cell">
										{t("financial.receivables.columns.received")}
									</TableHead>
									<TableHead className="text-right">
										{t("financial.receivables.columns.outstanding")}
									</TableHead>
									<TableHead>
										{t("financial.receivables.columns.status")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{rows.map((receivable) => (
									<TableRow
										key={receivable.id}
										className="cursor-pointer"
										onClick={() =>
											navigate({
												to: "/app/org/$orgId/financial/receivables/$receivableId",
												params: { orgId, receivableId: receivable.id },
											})
										}
									>
										<TableCell>
											<span className="font-medium">
												{receivable.buyerName}
											</span>
											{receivable.buyerDocument && (
												<span className="block text-xs text-base-content/60">
													{receivable.buyerDocument}
												</span>
											)}
										</TableCell>
										<TableCell className="hidden sm:table-cell">
											{formatDateOnly(new Date(receivable.saleDate).getTime())}
										</TableCell>
										<TableCell
											className={
												receivable.isOverdue ? "text-error" : undefined
											}
										>
											{receivable.dueDate
												? formatDateOnly(new Date(receivable.dueDate).getTime())
												: "—"}
										</TableCell>
										<TableCell className="text-right hidden md:table-cell">
											{formatMoney(receivable.expectedAmount, currency)}
										</TableCell>
										<TableCell className="text-right hidden md:table-cell text-success">
											{formatMoney(receivable.receivedAmount, currency)}
										</TableCell>
										<TableCell className="text-right font-medium">
											{formatMoney(receivable.outstandingAmount, currency)}
										</TableCell>
										<TableCell>
											<ReceivableStatusBadge
												status={receivable.displayStatus}
											/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>
			</div>
		</div>
	);
}
